import { useEffect, useState } from "react";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** Super-admin: permanently delete presence / shift time-tracking history
 *  (the data behind the In/Out Shift Times card and Coverage board). Does NOT
 *  touch users, permissions or the external Controlio productivity data. */
export const TimeTrackingReset = () => {
  const [scope, setScope] = useState("all");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    api.get("/inout/shifts")
      .then((r) => setPeople(r.data?.items || []))
      .catch(() => setPeople([]));
  }, []);

  const run = async () => {
    if (confirm.trim().toUpperCase() !== "RESET") return toast.error('Type RESET to confirm.');
    if (scope === "user" && !userId) return toast.error("Choose a person.");
    if (scope === "range" && !from && !to) return toast.error("Pick at least a from or to date.");
    const who = scope === "user" ? (people.find((p) => p.user_id === userId)?.name || "this person") : "everyone";
    const win = scope === "range" || (scope === "user" && (from || to)) ? ` for ${from || "…"} → ${to || "…"}` : "";
    if (!window.confirm(`Permanently delete presence/shift tracking history for ${who}${win}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/inout/reset-tracking", {
        scope, user_id: userId, from, to, confirm: confirm.trim(),
      });
      toast.success(`Reset done — ${r.data.deleted} tracking record(s) deleted (${r.data.label}).`);
      setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset time tracking.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/50 px-4 py-3 dark:border-rose-900/50 dark:bg-rose-950/20"
         data-testid="reset-time-tracking">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
        <div className="text-sm font-medium text-rose-900 dark:text-rose-200">Reset user time tracking</div>
      </div>
      <p className="mt-1 text-xs text-rose-800/70 dark:text-rose-300/70">
        Permanently deletes the presence/shift history behind the In/Out Shift Times card and Coverage board.
        Does not affect users, permissions, or the Controlio productivity data.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="reset-scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone · all history</SelectItem>
            <SelectItem value="range">Everyone · date range</SelectItem>
            <SelectItem value="user">A single person</SelectItem>
          </SelectContent>
        </Select>

        {scope === "user" && (
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="reset-user">
              <SelectValue placeholder="Choose person…" />
            </SelectTrigger>
            <SelectContent>
              {people.map((p) => (
                <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(scope === "range" || scope === "user") && (
          <>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                   className="h-8 w-[150px] text-xs" data-testid="reset-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   className="h-8 w-[150px] text-xs" data-testid="reset-to" />
          </>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
               placeholder='Type RESET to confirm' className="h-8 w-[200px] text-xs"
               data-testid="reset-confirm" />
        <Button size="sm" variant="destructive" onClick={run}
                disabled={busy || confirm.trim().toUpperCase() !== "RESET"}
                className="h-8 gap-1.5 text-xs" data-testid="reset-run">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Reset tracking
        </Button>
      </div>
    </div>
  );
};

export default TimeTrackingReset;
